import React, { useState, useRef, useEffect } from 'react';
import { motion, useDragControls } from 'framer-motion';
import './Window.css';

// Simple SVG Icons
const IconMinus = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="2" y1="6" x2="10" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IconX = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="2" y1="2" x2="10" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="10" y1="2" x2="2" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IconSquare = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="8" height="8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);

const IconDocument = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 1H7L9 3V11H3V1Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <line x1="4" y1="5" x2="8" y2="5" stroke="currentColor" strokeWidth="1"/>
    <line x1="4" y1="7" x2="8" y2="7" stroke="currentColor" strokeWidth="1"/>
  </svg>
);

const IconChevronDown = () => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 2L4 5L7 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconThreeDots = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
    <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
    <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
  </svg>
);

const Window = ({
  title,
  children,
  initialX = 100,
  initialY = 100,
  width = 600,
  height = 400,
  zIndex = 10,
  onClose,
  onMinimize,
  onFocus,
  onNavigate,
  id,
  allApps = []
}) => {
  const [position, setPosition] = useState({ x: initialX, y: initialY });
  const [size, setSize] = useState({ width, height });
  const [isDragging, setIsDragging] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [previousState, setPreviousState] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [wasSnapped, setWasSnapped] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [snapIndicator, setSnapIndicator] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTitleDropdownOpen, setIsTitleDropdownOpen] = useState(false);
  const [isSnapLayoutOpen, setIsSnapLayoutOpen] = useState(false);
  const [dragKey, setDragKey] = useState(0);

  const windowRef = useRef(null);
  const constraintsRef = useRef(null);
  const menuRef = useRef(null);
  const titleMenuRef = useRef(null);
  const snapLayoutRef = useRef(null);
  const snapLayoutTimerRef = useRef(null);
  const dragControls = useDragControls();
  const resizeStartPos = useRef({ x: 0, y: 0 });
  const resizeStartSize = useRef({ width: 0, height: 0 });
  const resizeStartWindowPos = useRef({ x: 0, y: 0 });

  const SNAP_THRESHOLD = 50;
  const MIN_WIDTH = 200;
  const MIN_HEIGHT = 150;
  const TITLE_BAR_HEIGHT = 44; // Title bar height from CSS

  // Handle window resize
  const handleResizeStart = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    resizeStartPos.current = { x: e.clientX, y: e.clientY };
    resizeStartSize.current = { ...size };
    resizeStartWindowPos.current = { ...position };
    if (onFocus) onFocus();
  };

  const handleResizeMove = (e) => {
    if (!isResizing || !resizeDirection) return;

    const deltaX = e.clientX - resizeStartPos.current.x;
    const deltaY = e.clientY - resizeStartPos.current.y;
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight - 40; // Account for taskbar

    let newWidth = resizeStartSize.current.width;
    let newHeight = resizeStartSize.current.height;
    let newX = resizeStartWindowPos.current.x;
    let newY = resizeStartWindowPos.current.y;

    if (resizeDirection.includes('right')) {
      newWidth = Math.max(MIN_WIDTH, Math.min(resizeStartSize.current.width + deltaX, screenWidth - newX));
    }
    if (resizeDirection.includes('left')) {
      const potentialWidth = resizeStartSize.current.width - deltaX;
      const potentialX = resizeStartWindowPos.current.x + deltaX;
      // Ensure window doesn't go past left edge and right edge stays within screen
      const maxX = screenWidth - MIN_WIDTH;
      if (potentialWidth >= MIN_WIDTH && potentialX >= 0 && potentialX <= maxX) {
        newWidth = potentialWidth;
        newX = potentialX;
      }
    }
    if (resizeDirection.includes('bottom')) {
      const maxHeight = screenHeight - newY;
      newHeight = Math.max(MIN_HEIGHT, Math.min(resizeStartSize.current.height + deltaY, maxHeight));
    }
    if (resizeDirection.includes('top')) {
      const potentialHeight = resizeStartSize.current.height - deltaY;
      const potentialY = resizeStartWindowPos.current.y + deltaY;
      // Ensure title bar never goes above screen and bottom stays within bounds
      const maxY = screenHeight - MIN_HEIGHT;
      if (potentialHeight >= MIN_HEIGHT && potentialY >= 0 && potentialY <= maxY) {
        newHeight = potentialHeight;
        newY = potentialY;
      }
    }

    setSize({ width: newWidth, height: newHeight });
    setPosition({ x: newX, y: newY });
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
    setResizeDirection(null);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, resizeDirection]);

  const handleDrag = (event, info) => {
    if (!isDragging) setIsDragging(true);

    // Get current mouse position
    const mouseX = event.clientX;
    const screenWidth = window.innerWidth;

    // Snap indicators based on mouse position
    if (mouseX < SNAP_THRESHOLD) {
      setSnapIndicator('left');
    } else if (mouseX > screenWidth - SNAP_THRESHOLD) {
      setSnapIndicator('right');
    } else {
      setSnapIndicator(null);
    }
  };

  const handleDragEnd = (event, info) => {
    setIsDragging(false);

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight - 40; // Account for taskbar
    const mouseX = event.clientX;

    // Check snap at the end based on final mouse position
    const shouldSnapLeft = mouseX < SNAP_THRESHOLD;
    const shouldSnapRight = mouseX > screenWidth - SNAP_THRESHOLD;

    // Clear snap indicator
    setSnapIndicator(null);

    // Calculate new position from drag
    const newX = position.x + info.offset.x;
    const newY = position.y + info.offset.y;

    // Snap to sides - save previous state before snapping
    if (shouldSnapLeft) {
      setPreviousState({ position: { ...position }, size: { ...size } });
      setPosition({ x: 0, y: 0 });
      setSize({ width: screenWidth / 2, height: screenHeight });
      setIsMaximized(false);
      setWasSnapped(true); // Mark as snapped
      setDragKey(prev => prev + 1); // Force re-render to reset drag state
      return;
    } else if (shouldSnapRight) {
      setPreviousState({ position: { ...position }, size: { ...size } });
      setPosition({ x: screenWidth / 2, y: 0 });
      setSize({ width: screenWidth / 2, height: screenHeight });
      setIsMaximized(false);
      setWasSnapped(true); // Mark as snapped
      setDragKey(prev => prev + 1); // Force re-render to reset drag state
      return;
    }

    // Normal drag - constrain to viewport boundaries
    // Keep title bar fully visible - never allow it to go outside desktop
    const constrainedX = Math.max(0, Math.min(newX, screenWidth - size.width));
    const constrainedY = Math.max(0, Math.min(newY, screenHeight - TITLE_BAR_HEIGHT));

    setPosition({
      x: constrainedX,
      y: constrainedY
    });

    // If we moved the window, it's no longer snapped
    if (Math.abs(info.offset.x) > 5 || Math.abs(info.offset.y) > 5) {
      setWasSnapped(false);
    }
  };

  const handleMaximize = () => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight - 40;

    if (!isMaximized) {
      setPreviousState({ position: { ...position }, size: { ...size } });
      setPosition({ x: 0, y: 0 });
      setSize({
        width: screenWidth,
        height: screenHeight
      });
    } else {
      // If window was snapped before maximizing, center it with a smaller size
      if (wasSnapped) {
        const newWidth = Math.min(width, 800); // Default to smaller width
        const newHeight = Math.min(height, 600); // Default to smaller height
        const centeredX = (screenWidth - newWidth) / 2;
        const centeredY = (screenHeight - newHeight) / 2;

        setPosition({ x: centeredX, y: centeredY });
        setSize({ width: newWidth, height: newHeight });
        setWasSnapped(false); // Reset snapped state
      } else if (previousState) {
        // Normal restore - go back to previous state
        setPosition(previousState.position);
        setSize(previousState.size);
      }
    }
    setIsMaximized(!isMaximized);
  };

  const handleDoubleClickTitleBar = () => {
    handleMaximize();
  };

  // Handle snap layout selection
  const handleSnapLayout = (layout) => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight - 40; // Account for taskbar

    setPreviousState({ position: { ...position }, size: { ...size } });

    switch (layout) {
      case 'left':
        setPosition({ x: 0, y: 0 });
        setSize({ width: screenWidth / 2, height: screenHeight });
        break;
      case 'right':
        setPosition({ x: screenWidth / 2, y: 0 });
        setSize({ width: screenWidth / 2, height: screenHeight });
        break;
      case 'top-left':
        setPosition({ x: 0, y: 0 });
        setSize({ width: screenWidth / 2, height: screenHeight / 2 });
        break;
      case 'top-right':
        setPosition({ x: screenWidth / 2, y: 0 });
        setSize({ width: screenWidth / 2, height: screenHeight / 2 });
        break;
      case 'bottom-left':
        setPosition({ x: 0, y: screenHeight / 2 });
        setSize({ width: screenWidth / 2, height: screenHeight / 2 });
        break;
      case 'bottom-right':
        setPosition({ x: screenWidth / 2, y: screenHeight / 2 });
        setSize({ width: screenWidth / 2, height: screenHeight / 2 });
        break;
      default:
        break;
    }

    setIsSnapLayoutOpen(false);
    setIsMaximized(false);
    setWasSnapped(true); // Mark as snapped
    setDragKey(prev => prev + 1); // Force re-render to reset drag state
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (titleMenuRef.current && !titleMenuRef.current.contains(event.target)) {
        setIsTitleDropdownOpen(false);
      }
      if (snapLayoutRef.current && !snapLayoutRef.current.contains(event.target)) {
        setIsSnapLayoutOpen(false);
      }
    };

    if (isMenuOpen || isTitleDropdownOpen || isSnapLayoutOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMenuOpen, isTitleDropdownOpen, isSnapLayoutOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Shift + W to close window
      if (event.shiftKey && event.key.toLowerCase() === 'w') {
        event.preventDefault();
        onClose(id);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [id, onClose]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (snapLayoutTimerRef.current) {
        clearTimeout(snapLayoutTimerRef.current);
      }
    };
  }, []);

  // Copy link handler
  const handleCopyLink = () => {
    const url = window.location.origin + '/#' + id;
    navigator.clipboard.writeText(url).then(() => {
      console.log('Link copied:', url);
      // Could show a toast notification here
    }).catch(err => {
      console.error('Failed to copy link:', err);
    });
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Snap indicator overlay */}
      {snapIndicator && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            left: snapIndicator === 'left' ? 0 : '50%',
            top: 0,
            width: '50%',
            height: 'calc(100vh - 40px)',
            backgroundColor: '#4299e1',
            border: '2px solid #2b6cb0',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        />
      )}

      <motion.div
        key={dragKey}
        ref={windowRef}
        className={`window ${isMaximized ? 'maximized' : ''} ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''}`}
        style={{
          position: 'absolute',
          width: size.width,
          height: size.height,
          zIndex: zIndex,
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (onFocus) onFocus();
        }}
        initial={{ opacity: 0, scale: 0.9, x: position.x, y: position.y }}
        animate={{
          x: position.x,
          y: position.y,
          opacity: 1,
          scale: 1,
        }}
        transition={{
          duration: 0.2,
          ease: "easeOut",
        }}
        exit={{ opacity: 0, scale: 0.9 }}
        drag={!isMaximized && !isResizing}
        dragControls={dragControls}
        dragListener={false}
        dragMomentum={false}
        dragElastic={0}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
      >
        <div
          className="window-title-bar"
          onPointerDown={(e) => {
            // Don't start drag if clicking on window control buttons or if actively clicking inside dropdowns
            const clickedElement = e.target;
            const isControlButton = clickedElement.closest('.window-controls');
            const isInsideDropdown = clickedElement.closest('.window-title-dropdown') ||
                                   clickedElement.closest('.window-dropdown-menu');

            if (isControlButton || isInsideDropdown) {
              return;
            }

            // Prevent desktop selection when dragging window
            e.preventDefault();
            e.stopPropagation();
            dragControls.start(e);
          }}
          onDoubleClick={handleDoubleClickTitleBar}
        >
          <div className="window-title-center">
            <div className="window-title-group" ref={titleMenuRef}>
              <button
                className="window-title-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsTitleDropdownOpen(!isTitleDropdownOpen);
                }}
                title="Navigate to page"
              >
                <span className="window-title">{title}</span>
                <IconChevronDown />
              </button>
              <button
                className="window-menu-dots-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(!isMenuOpen);
                }}
                title="Window Menu"
                ref={menuRef}
              >
                <IconThreeDots />
              </button>
              {isTitleDropdownOpen && (
                <div className="window-title-dropdown">
                  {allApps.map((app) => (
                    <div
                      key={app.id}
                      className={`window-menu-item ${app.name === title ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsTitleDropdownOpen(false);
                        if (onNavigate) {
                          onNavigate(app);
                        }
                      }}
                    >
                      <img src={app.icon} alt="" className="menu-item-icon" />
                      <span>{app.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {isMenuOpen && (
                <div className="window-dropdown-menu">
                  <div
                    className="window-menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyLink();
                    }}
                  >
                    <span>Copy Link</span>
                  </div>
                  <div
                    className="window-menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMenuOpen(false);
                      // Bookmark functionality can be added here
                      console.log('Bookmark clicked');
                    }}
                  >
                    <span>Bookmark</span>
                  </div>
                  <div className="window-menu-divider" />
                  <div
                    className="window-menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMenuOpen(false);
                      onClose(id);
                    }}
                  >
                    <span>Close</span>
                    <div className="window-menu-shortcut">
                      <span className="shortcut-key">Shift</span>
                      <span className="shortcut-key">W</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="window-controls">
            <button
              className="window-btn minimize"
              onClick={(e) => {
                e.stopPropagation();
                onMinimize();
              }}
              title="Minimize"
            >
              <IconMinus />
            </button>
            <div
              className="maximize-btn-container"
              ref={snapLayoutRef}
              onMouseEnter={() => {
                // Clear any existing timer
                if (snapLayoutTimerRef.current) {
                  clearTimeout(snapLayoutTimerRef.current);
                }
                // Show menu after a short delay
                snapLayoutTimerRef.current = setTimeout(() => {
                  setIsSnapLayoutOpen(true);
                }, 300);
              }}
              onMouseLeave={(e) => {
                // Clear the timer if mouse leaves before menu opens
                if (snapLayoutTimerRef.current) {
                  clearTimeout(snapLayoutTimerRef.current);
                }
                // Only close if not hovering over the menu itself
                if (!snapLayoutRef.current?.contains(e.relatedTarget)) {
                  // Add delay before closing
                  snapLayoutTimerRef.current = setTimeout(() => {
                    setIsSnapLayoutOpen(false);
                  }, 200);
                }
              }}
            >
              <button
                className="window-btn maximize"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMaximize();
                }}
                title="Maximize"
              >
                <IconSquare />
              </button>
              {isSnapLayoutOpen && (
                <div className="snap-layout-menu">
                  <div className="snap-layout-grid">
                    <div
                      className="snap-layout-option snap-left"
                      onClick={() => handleSnapLayout('left')}
                      title="Snap to left half"
                    >
                      <div className="snap-preview">
                        <div className="snap-preview-active left"></div>
                        <div className="snap-preview-inactive right"></div>
                      </div>
                    </div>
                    <div
                      className="snap-layout-option snap-right"
                      onClick={() => handleSnapLayout('right')}
                      title="Snap to right half"
                    >
                      <div className="snap-preview">
                        <div className="snap-preview-inactive left"></div>
                        <div className="snap-preview-active right"></div>
                      </div>
                    </div>
                    <div
                      className="snap-layout-option snap-top-left"
                      onClick={() => handleSnapLayout('top-left')}
                      title="Snap to top-left quarter"
                    >
                      <div className="snap-preview quarters">
                        <div className="snap-preview-active"></div>
                        <div className="snap-preview-inactive"></div>
                        <div className="snap-preview-inactive"></div>
                        <div className="snap-preview-inactive"></div>
                      </div>
                    </div>
                    <div
                      className="snap-layout-option snap-top-right"
                      onClick={() => handleSnapLayout('top-right')}
                      title="Snap to top-right quarter"
                    >
                      <div className="snap-preview quarters">
                        <div className="snap-preview-inactive"></div>
                        <div className="snap-preview-active"></div>
                        <div className="snap-preview-inactive"></div>
                        <div className="snap-preview-inactive"></div>
                      </div>
                    </div>
                    <div
                      className="snap-layout-option snap-bottom-left"
                      onClick={() => handleSnapLayout('bottom-left')}
                      title="Snap to bottom-left quarter"
                    >
                      <div className="snap-preview quarters">
                        <div className="snap-preview-inactive"></div>
                        <div className="snap-preview-inactive"></div>
                        <div className="snap-preview-active"></div>
                        <div className="snap-preview-inactive"></div>
                      </div>
                    </div>
                    <div
                      className="snap-layout-option snap-bottom-right"
                      onClick={() => handleSnapLayout('bottom-right')}
                      title="Snap to bottom-right quarter"
                    >
                      <div className="snap-preview quarters">
                        <div className="snap-preview-inactive"></div>
                        <div className="snap-preview-inactive"></div>
                        <div className="snap-preview-inactive"></div>
                        <div className="snap-preview-active"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button
              className="window-btn close"
              onClick={(e) => {
                e.stopPropagation();
                onClose(id);
              }}
              title="Close"
            >
              <IconX />
            </button>
          </div>
        </div>

        <div className="window-content">
          {children}
        </div>

        {/* Resize handles */}
        {!isMaximized && (
          <>
            {/* Edges */}
            <div
              className="resize-handle resize-top"
              onMouseDown={(e) => handleResizeStart(e, 'top')}
            />
            <div
              className="resize-handle resize-right"
              onMouseDown={(e) => handleResizeStart(e, 'right')}
            />
            <div
              className="resize-handle resize-bottom"
              onMouseDown={(e) => handleResizeStart(e, 'bottom')}
            />
            <div
              className="resize-handle resize-left"
              onMouseDown={(e) => handleResizeStart(e, 'left')}
            />

            {/* Corners */}
            <div
              className="resize-handle resize-top-left"
              onMouseDown={(e) => handleResizeStart(e, 'top-left')}
            />
            <div
              className="resize-handle resize-top-right"
              onMouseDown={(e) => handleResizeStart(e, 'top-right')}
            />
            <div
              className="resize-handle resize-bottom-left"
              onMouseDown={(e) => handleResizeStart(e, 'bottom-left')}
            />
            <div
              className="resize-handle resize-bottom-right"
              onMouseDown={(e) => handleResizeStart(e, 'bottom-right')}
            />
          </>
        )}
      </motion.div>
    </>
  );
};

export default Window;
